import React, { useState, useEffect } from 'react';
import useAPIResReqStore, { emptyCurlTemplate } from '../../useAPIResReqStore';
import {
  MenuItem,
  Button,
  Grid,
  styled,
  Typography,
  Box,
  Autocomplete,
  TextField,
  Checkbox,
  FormControlLabel,
  Skeleton,
  Snackbar,
} from '@mui/material';
import { send_request } from '../../../../utils/Request';
import MentionEditor from '../../../TextEditor/MentionEditor';
import DynamicIcons from '../../../CredentialStore/DynamicIcons';
import ElectricalServicesIcon from '@mui/icons-material/ElectricalServices';
import W86AutoComplete from '../../../Component/W86AutoComplete';
import WarningIcon from '@mui/icons-material/Warning';
import CodeIcon from '@mui/icons-material/Code';
import '../../APIResReqStyle.css'; // Make sure to import the CSS file
import CurlTemplateSkeleton from './CurlTemplateSkeleton';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useNavigate } from 'react-router-dom';

const GenerateCurlButton = styled(Button)(({ theme }) => ({
  '&.Mui-disabled': {
    background: theme.palette.action.disabledBackground,
  },
  '&:not(.Mui-disabled)': {
    backgroundColor: theme.palette.text.primary,
    color: theme.palette.background.paper,
    '&:hover': {
      backgroundColor: theme.palette.text.secondary,
    },
  },
  width: '100%',
  height: '34px',
  padding: '8px 24px 8px 24px',
  gap: '10px',
  borderRadius: '4px',
  textTransform: 'none',
}));

const SelectLabel = styled(Typography)({
  paddingBottom: '8px',
  fontFamily: 'Open Sans !important',
  fontSize: '14px',
  fontWeight: '400 !important',
  lineHeight: '19.07px',
  textAlign: 'left',
});

const DescriptionLabel = styled(Typography)(({ theme }) => ({
  paddingBottom: '8px',
  fontFamily: 'Open Sans !important',
  fontSize: '14px',
  fontWeight: '400 !important',
  lineHeight: '19.07px',
  textAlign: 'left',
  color: theme.palette.text.secondary,
}));

const CredentialsMenuItem = styled(MenuItem)({
  display: 'flex',
  alignItems: 'center',
  '& > *:first-of-type': {
    marginRight: '16px',
  },
});

const TitleText = styled(Typography)(({ theme }) => ({
  fontFamily: 'Open Sans',
  fontSize: '14px',
  fontWeight: '700 !important',
  lineHeight: '19.07px',
  textAlign: 'left',
  color: theme.palette.text.primary,
}));

const ConnectAppButton = styled(Button)(({ theme }) => ({
  height: '32px',
  padding: '6px 16px 6px 16px',
  borderRadius: '4px',
  backgroundColor: theme.palette.text.primary,
  color: theme.palette.background.paper,
  textTransform: 'none',
  '&:hover': {
    backgroundColor: theme.palette.text.secondary,
  },
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'flex-start',
  minWidth: '117px',
  '& .MuiButton-startIcon': {
    marginLeft: '0',
    marginRight: '8px',
  },
}));

const CredentialRefreshButton = styled(Button)({
  width: '72px',
  height: '32px',
  padding: '6px 16px',
  gap: '4px',
  borderRadius: '4px',
  border: '1px solid',
  opacity: 1,
  textTransform: 'none',
  marginLeft: '8px',
});

const ACTION_TEMPLATES_CREDENTIALS_URL = 'ai-service/credential/user/action-templates';
const DEFAULT_SECRET_PLACEHOLDER = '${access_token.SECRET}';
const CREDENTIAL_PAGE_URL = '/credential_store';
const BLANK = '_blank';

const NO_CREDENTIAL_TEXT = 'No credentials available';
const SELECT_ACTION_PLACEHOLDER = 'Select Action';
const CURL_SETUP_MODE = 'CURL';
const SELECT_APP_PLACEHOLDER = 'Select App';
const SELECT_CREDENTIALS_PLACEHOLDER = 'Select Credential';
const ON_FOCUS_INPUT = 'rawResponsePlaceholderName';

const APP_DONT_EXIST = 'The app you have selected previously is no longer available';
const FIELD_DONT_EXIST =
  'The action fields have changed, fields that were saved previously are now overwritten';
const ACTION_DONT_EXIST = 'Action has not been selected or could not be found';
const APP_COMING_SOON = 'Apps coming soon to Workflow86';
const COMING_SOON_SUFFIX = '(Coming Soon)';

const StyledSnackbar = styled(Snackbar)({
  '& .MuiSnackbarContent-root': {
    minWidth: 'unset',
    backgroundColor: '#55A77A',
    padding: '6px 16px',
  },
});

const SnackbarContent = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
  },
});

const FieldsLabel = styled(DescriptionLabel)({
  paddingBottom: '0px !important',
});

const AppIconCount = styled(Typography)({
  marginLeft: '4px',
});

const NoCredentialsText = styled(Typography)({
  marginBottom: '10px',
  paddingTop: '8px',
});

const ErrorText = styled(Typography)({
  marginTop: '4px',
  color: '#FF8C00',
});

const CurlTemplatesSetup = ({ availablePlaceholders, setOnFocus, componentStore = useAPIResReqStore }) => {
  const [selectedApp, setSelectedApp] = useState('');
  const [selectedAction, setSelectedAction] = useState('');
  const [fields, setFields] = useState([]);
  const [curl, setCurl] = useState('');
  const [userCredentials, setUserCredentials] = useState([]);
  const [secretPlaceholders, setSecretPlaceholders] = useState([]);
  const [selectedCredential, setSelectedCredential] = useState('');
  const [credentialAdditionalFields, setCredentialAdditionalFields] = useState({});
  const [templates, setTemplates] = useState([]);
  const [useIntegrationsTemplate, setUseIntegrationsTemplate] = useState(false);
  const [templateNotExistError, setTemplateNotExistError] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [openSnackbar, setOpenSnackbar] = useState(false);

  const extractCredentialAdditionalFields = (acc, cred) => {
    const additionalFields = cred.data?.additionalFieldInputs || {};
    Object.entries(additionalFields).forEach(([key, value]) => {
      acc[`%${key}%`] = value;
    });
    return acc;
  };

  const updateCredentialAdditionalFields = (selectedCredential, userCredentials) => {
    if (!selectedCredential) {
      setCredentialAdditionalFields({});
      return;
    }
    const matchedCredentials = userCredentials.filter(
      credential => credential.data?.placeholder === selectedCredential
    );
    if (matchedCredentials.length === 0) {
      setCredentialAdditionalFields({});
      return;
    }
    setCredentialAdditionalFields(matchedCredentials.reduce(extractCredentialAdditionalFields, {}));
  };

  useEffect(() => {
    updateCredentialAdditionalFields(selectedCredential, userCredentials);
  }, [selectedCredential, userCredentials]);

  const getAppCredentials = credentials => {
    return credentials
      .filter(credential => credential?.data?.integrationType)
      .map(credential => ({
        ...credential,
        data: credential.data || {},
      }));
  };

  const processActionTemplates = actionTemplates => {
    return actionTemplates?.map(template => ({
      ...template,
      actions: template.actions?.map(action => ({
        ...action,
        fields: action.fields?.map(field => ({
          ...field,
          value: field.defaultValue || '',
        })),
      })),
    }));
  };

  const actionComparator = () => {
    return (a, b) => {
      // Sort coming soon items to the bottom
      const aComingSoon = a.appName.includes(COMING_SOON_SUFFIX);
      const bComingSoon = b.appName.includes(COMING_SOON_SUFFIX);
      if (aComingSoon && !bComingSoon) return 1;
      if (!aComingSoon && bComingSoon) return -1;
      return a.appName.localeCompare(b.appName);
    };
  };

  const addComingSoonDivider = () => {
    return (template, index, array) => {
      const currentComingSoon = template.appName.includes(COMING_SOON_SUFFIX);
      const prevComingSoon = index > 0 && array[index - 1].appName.includes(COMING_SOON_SUFFIX);

      // Add a divider and header before the first "Coming Soon" item
      if (currentComingSoon && !prevComingSoon) {
        return { ...template, divider: true, header: APP_COMING_SOON };
      }
      return template;
    };
  };

  const getUserCredentials = async () => {
    try {
      const res = await send_request(ACTION_TEMPLATES_CREDENTIALS_URL, '', '', 'GET');
      if (!res && !res.data) return;

      const data = res.data;
      if (data.credentials) {
        const credentialData = getAppCredentials(data.credentials);
        setUserCredentials(credentialData);
      }

      if (data.actionTemplates) {
        const processedActionTemplates = processActionTemplates(data.actionTemplates)
          .sort(actionComparator())
          .map(addComingSoonDivider());
        setTemplates(processedActionTemplates);
        loadCurrentCurlTemplate(processedActionTemplates);
      }
    } catch (err) {
      console.log(err);
    }
  };

  const setSecretPlaceholderByApp = appName => {
    const placeholderList = userCredentials.filter(
      credential => credential.data.integrationType === appName
    );
    setSecretPlaceholders(placeholderList);
    return placeholderList;
  };

  const resetCurlTemplateState = () => {
    resetTemplateState();
    componentStore.getState().initialiseCurlTemplateEmpty();
    setSelectedApp('');
  };

  useEffect(() => {
    if (selectedApp && userCredentials.length > 0) {
      setSecretPlaceholderByApp(selectedApp);
    }
  }, [selectedApp, userCredentials]);

  useEffect(() => {
    getUserCredentials();
  }, []);

  const loadFieldFromTemplate = (actionFromTemplate, persistedCurlTemplate) => {
    const hasSameFields = compareActionFields(actionFromTemplate, persistedCurlTemplate.action);
    if (!hasSameFields) {
      setTemplateNotExistError(FIELD_DONT_EXIST);
      setFields(actionFromTemplate.fields);
    } else {
      setFields(persistedCurlTemplate.action.fields);
    }
  };

  const validatePersistedCredential = persistedCurlTemplate => {
    const persistedCredentialsPH = persistedCurlTemplate.credentialPlaceholder;
    if (persistedCredentialsPH) {
      setSelectedCredential(persistedCredentialsPH);
    }
  };

  const loadActionFromTemplate = (actionFromTemplate, persistedCurlTemplate) => {
    if (actionFromTemplate) {
      loadAction(persistedCurlTemplate.action);

      if (actionFromTemplate && actionFromTemplate.fields) {
        loadFieldFromTemplate(actionFromTemplate, persistedCurlTemplate);
      }
    } else {
      setTemplateNotExistError(ACTION_DONT_EXIST);
      setSelectedAction(undefined);
    }
  };

  const handleInvalidAppFromTemplate = () => {
    setTemplateNotExistError(APP_DONT_EXIST);
    resetTemplateState();
    setIsLoading(false);
  };

  const loadCurrentCurlTemplate = allTemplates => {
    setIsLoading(true);
    const persistedCurlTemplate = componentStore.getState().curlTemplateData;

    const isPersistedCurlTemplateEmpty =
      JSON.stringify(persistedCurlTemplate) === JSON.stringify(emptyCurlTemplate());

    if (persistedCurlTemplate && !isPersistedCurlTemplateEmpty) {
      setUseIntegrationsTemplate(true);

      const appFromTemplate = allTemplates.find(
        template => template.appName === persistedCurlTemplate.appName
      );
      if (appFromTemplate) {
        setSelectedApp(persistedCurlTemplate.appName);
      } else {
        handleInvalidAppFromTemplate();
        return;
      }
      validatePersistedCredential(persistedCurlTemplate);

      const actionFromTemplate = appFromTemplate.actions.find(
        action => action.action === persistedCurlTemplate.action.action
      );
      loadActionFromTemplate(actionFromTemplate, persistedCurlTemplate);
    }
    setIsLoading(false);
  };
  const interpolateWithAdditionalFieldValue = field => {
    const additionalFieldValue = credentialAdditionalFields[field?.value];
    if (additionalFieldValue) {
      field.value = additionalFieldValue;
      return field;
    }
    return field;
  };

  const loadAction = actionObject => {
    if (!actionObject) {
      resetTemplateState();
      return;
    }
    const actionFields = actionObject.fields;
    setSelectedAction(actionObject.action);
    setFields(actionFields.map(interpolateWithAdditionalFieldValue));
    setCurl(actionObject.curl);
  };

  const resetTemplateState = () => {
    setSelectedAction('');
    setFields([]);
    setCurl('');
    setSelectedCredential('');
  };

  const handleAppChange = (event, newValue) => {
    const newValueAppName = newValue ? newValue.appName : '';
    setSelectedApp(newValueAppName);
    resetTemplateState();
    componentStore.getState().setCurlTemplateDataAppName(newValueAppName);
    setTemplateNotExistError('');
  };

  const handleActionChange = (event, newValue) => {
    if (!newValue) {
      resetTemplateState();
      return;
    }
    const app = templates.find(t => t.appName === selectedApp);
    const action = app.actions.find(a => a.action === newValue);
    loadAction(action);
    componentStore.getState().setCurlTemplateDataAction(action);
    setTemplateNotExistError('');

    // Generate request if we have all required data
    if (selectedCredential && action.fields) {
      const hasEmptyRequired = action.fields.some(field => field.required === true && !field.value);
      if (!hasEmptyRequired) {
        let generatedCurl = action.curl;
        action.fields.forEach(field => {
          const placeholder = field.placeholder || `{{${field.name}}}`;
          const regex = new RegExp(placeholder, 'g');
          generatedCurl = generatedCurl.replace(regex, field.value);
        });

        if (selectedCredential) {
          generatedCurl = generatedCurl.replace(
            DEFAULT_SECRET_PLACEHOLDER,
            '${' + selectedCredential + '.SECRET}'
          );
        }

        componentStore.getState().setRequestCurl(generatedCurl);
        setOpenSnackbar(true);
      }
    }
  };

  const updateField = (fields, index, newValue, isValid, field) => {
    const newFields = [...fields];
    newFields[index] = {
      ...newFields[index],
      value: newValue,
      error: isValid
        ? null
        : `Potentially invalid value for ${field.name}: Must satisfy ${field.regex}`,
    };
    return newFields;
  };

  const handleFieldChange = (index, newValue, field, availablePlaceholders) => {
    const containsValidPlaceholder = availablePlaceholders?.[0]?.placeholders?.some(placeholder =>
      newValue.includes(`\${${placeholder.key}}`)
    );

    let isValid = true;
    if (!containsValidPlaceholder && field.regex) {
      const valueForRegexCheck = newValue.replace(/\$\{[^}]+\}/g, '');
      isValid = new RegExp(field.regex).test(valueForRegexCheck);
    }

    const updatedFields = updateField(fields, index, newValue, isValid, field);

    setFields(updatedFields);
    componentStore.getState().setCurlTemplateDataFields(updatedFields);
    setTemplateNotExistError('');

    // Generate request after field update if we have all required data
    const hasEmptyRequired = updatedFields.some(field => field.required === true && !field.value);
    if (selectedAction && selectedCredential && !hasEmptyRequired) {
      // Use the updated fields for generating the request
      let generatedCurl = curl;
      updatedFields.forEach(field => {
        const placeholder = field.placeholder || `{{${field.name}}}`;
        const regex = new RegExp(placeholder, 'g');
        generatedCurl = generatedCurl.replace(regex, field.value);
      });

      if (selectedCredential) {
        generatedCurl = generatedCurl.replace(
          DEFAULT_SECRET_PLACEHOLDER,
          '${' + selectedCredential + '.SECRET}'
        );
      }

      componentStore.getState().setRequestCurl(generatedCurl);
      setOpenSnackbar(true);
    }
  };

  const handleCredentialChange = (event, newValue) => {
    const newCredentialPlaceholder = newValue ? newValue.value : '';
    setSelectedCredential(newCredentialPlaceholder);
    componentStore.getState().setCurlTemplateDataCredentialPlaceholder(newCredentialPlaceholder);
  };

  const generateRequest = () => {
    let generatedCurl = curl;
    fields.forEach(field => {
      const placeholder = field.placeholder || `{{${field.name}}}`;
      const regex = new RegExp(placeholder, 'g');
      generatedCurl = generatedCurl.replace(regex, field.value);
    });

    if (selectedCredential) {
      generatedCurl = generatedCurl.replace(
        DEFAULT_SECRET_PLACEHOLDER,
        '${' + selectedCredential + '.SECRET}'
      );
    }

    componentStore.getState().setRequestCurl(generatedCurl);
    setOpenSnackbar(true);
  };

  const hasEmptyRequiredFields = fields?.some(field => field.required === true && !field.value);
  const handleConnectApp = appName => {
    const searchParams = new URLSearchParams();
    searchParams.set('filter', appName);
    searchParams.set('showDialog', 'true');
    window.open(
      `${window.location.origin}${CREDENTIAL_PAGE_URL}?${searchParams.toString()}`,
      '_blank'
    );
  };

  const selectedAppTemplate = templates.find(app => app.appName === selectedApp);

  const compareActionFields = (action1, action2) => {
    const fields1 = action1.fields?.map(field => field.name).sort() || [];
    const fields2 = action2.fields?.map(field => field.name).sort() || [];
    return JSON.stringify(fields1) === JSON.stringify(fields2);
  };

  const handleSingleValueField = (index, newValue, field) => {
    handleFieldChange(index, newValue, field, availablePlaceholders);
  };

  const handleMultiValueField = (index, newValue, field) => {
    handleFieldChange(index, newValue.join(field?.delimiter || ','), field, availablePlaceholders);
  };

  const getSingleSelectField = (field, index, curlFieldRenderInput) => {
    return (
      <Autocomplete
        value={field.value || null}
        onChange={(_, newValue) => handleSingleValueField(index, newValue, field)}
        options={field.options || []}
        renderInput={curlFieldRenderInput}
      />
    );
  };

  const getMultiSelectField = (field, index, curlFieldRenderInput) => {
    return (
      <Autocomplete
        multiple
        value={field.value ? field.value.split(field?.delimiter || ',') : []}
        onChange={(_, newValue) => handleMultiValueField(index, newValue, field)}
        options={field.options || []}
        renderInput={curlFieldRenderInput}
      />
    );
  };

  const getStringInputField = (field, index) => {
    return (
      <MentionEditor
        availablePlaceholders={availablePlaceholders}
        value={field.value || ''}
        text={field.value || ''}
        onChange={newValue => handleSingleValueField(index, newValue, field)}
        onFocus={() => setOnFocus(ON_FOCUS_INPUT)}
      />
    );
  };

  const getFieldInputComponent = (field, index) => {
    const curlFieldRenderInput = params => (
      <TextField {...params} size="small" placeholder={`Select ${field.name}`} />
    );

    switch (field.type) {
      case 'singleSelect':
        return getSingleSelectField(field, index, curlFieldRenderInput);
      case 'multiSelect':
        return getMultiSelectField(field, index, curlFieldRenderInput);
      default:
        return getStringInputField(field, index);
    }
  };

  const toggleIntegrationTemplate = e => {
    const isToggled = e.target.checked;

    setUseIntegrationsTemplate(isToggled);
    if (!isToggled) {
      resetCurlTemplateState();
    }
  };

  return (
    <div
      className="setup-mode-container"
      hidden={componentStore.getState().requestData.setupMode !== CURL_SETUP_MODE}
      style={{ width: '100%' }}
    >
      {isLoading ? (
        <CurlTemplateSkeleton />
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            {templateNotExistError && (
              <div className="warning-banner">
                <WarningIcon />
                <span className="warning-text">{templateNotExistError}</span>
              </div>
            )}
            <FormControlLabel
              control={
                <Checkbox checked={useIntegrationsTemplate} onChange={toggleIntegrationTemplate} />
              }
              label={<TitleText>Use app integration template</TitleText>}
            />
            <DescriptionLabel>
              Use a pre-configured template to set up the request to an app connected in the{' '}
              <a
                href={`${window.location.origin}${CREDENTIAL_PAGE_URL}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                credentials page
              </a>
            </DescriptionLabel>
            {!useIntegrationsTemplate && (
              <Box className="app-icons-container">
                {templates.slice(0, 3)?.map((template, index) => (
                  <Box key={index} marginRight="4px">
                    <DynamicIcons icons={[template.icon]} />
                  </Box>
                ))}
                {templates?.length > 3 && (
                  <AppIconCount variant="body2">+{templates.length - 3}</AppIconCount>
                )}
              </Box>
            )}
          </Grid>

          {useIntegrationsTemplate && (
            <>
              <Grid item xs={12}>
                <W86AutoComplete
                  title="Select an app"
                  value={selectedAppTemplate || null}
                  onChange={handleAppChange}
                  options={templates}
                  renderOptionLabel={option => option.appName}
                  inputPlaceholder={SELECT_APP_PLACEHOLDER}
                />
              </Grid>

              <Grid item xs={12}>
                {selectedApp && secretPlaceholders?.length === 0 ? (
                  <>
                    <NoCredentialsText variant="body2">
                      No Credentials available for {selectedApp}
                    </NoCredentialsText>
                    <Box display="flex" alignItems="center">
                      <ConnectAppButton
                        variant="contained"
                        onClick={() => handleConnectApp(selectedApp)}
                        startIcon={<ElectricalServicesIcon />}
                      >
                        Connect app
                      </ConnectAppButton>
                      <CredentialRefreshButton variant="outlined" onClick={getUserCredentials}>
                        Refresh
                      </CredentialRefreshButton>
                    </Box>
                  </>
                ) : (
                  <W86AutoComplete
                    title="Select a credential"
                    inputPlaceholder={SELECT_CREDENTIALS_PLACEHOLDER}
                    renderOptionLabel={option => option.label}
                    value={
                      selectedCredential
                        ? { label: `\${${selectedCredential}.SECRET}`, value: selectedCredential }
                        : null
                    }
                    onChange={handleCredentialChange}
                    options={secretPlaceholders.map(credential => ({
                      label: `\${${credential.data.placeholder}.SECRET}`,
                      value: credential.data.placeholder,
                    }))}
                    renderInput={params => (
                      <TextField
                        {...params}
                        placeholder={SELECT_CREDENTIALS_PLACEHOLDER}
                        size="small"
                      />
                    )}
                    disabled={!selectedApp}
                    fullWidth
                    noOptionsText={NO_CREDENTIAL_TEXT}
                  />
                )}
              </Grid>

              <Grid item xs={12}>
                <W86AutoComplete
                  title="Select an action"
                  value={selectedAction}
                  onChange={handleActionChange}
                  inputPlaceholder={SELECT_ACTION_PLACEHOLDER}
                  options={
                    selectedApp
                      ? templates
                          .find(t => t.appName === selectedApp)
                          .actions?.map(action => action.action)
                      : []
                  }
                  disabled={!selectedApp}
                />
              </Grid>

              {fields?.length > 0 && (
                <Grid item>
                  <FieldsLabel>Fields</FieldsLabel>
                </Grid>
              )}

              {fields?.map((field, index) => (
                <Grid item xs={12} key={index}>
                  <SelectLabel>{`${field.name} ${field.required === true ? '*' : ''}`}</SelectLabel>
                  <DescriptionLabel>{field.description}</DescriptionLabel>
                  {getFieldInputComponent(field, index)}
                  {field.error && (
                    <ErrorText variant="caption">{field.errorMessage || field.error}</ErrorText>
                  )}
                </Grid>
              ))}
              <Grid item xs={12}>
                <GenerateCurlButton
                  variant="contained"
                  onClick={generateRequest}
                  disabled={!selectedAction || !selectedCredential || hasEmptyRequiredFields}
                  startIcon={<CodeIcon />}
                >
                  Generate Request
                </GenerateCurlButton>
              </Grid>
            </>
          )}
        </Grid>
      )}
      <StyledSnackbar
        open={openSnackbar}
        autoHideDuration={500}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={
          <SnackbarContent>
            <CheckCircleIcon />
            Request updated
          </SnackbarContent>
        }
      />
    </div>
  );
};

export default CurlTemplatesSetup;
